import { setCookie } from "cookies-next/client";

import publicConfig from "@/config/public";
import { Cart, CartChange } from "@/custom-types/Cart";
import {
  LOAD_COOKIES,
  LoadCookiesAction,
} from "@/redux/action-creators/cookies";
import { HYDRATE, HydrateAction } from "@/redux/reducers/hydration";

import { ScheduleWindow } from "@/components/Checkout/ScheduledOrderModal/ScheduledOrderModal";

const X_LEAFLY_COOKIE_NAME = "X-Leafly-Id";

const { cookieDomain, cookieDomainCa } = publicConfig;

export type ContinueShopping = {
  dispensaryPath?: string;
  brandIsPaid?: boolean;
};
export type CartState = {
  sessionId: string;
  changes: CartChange[] | null;
  data: Cart | null;
  isCheckingPhone: boolean;
  phoneError: string | null;
  selectedForDeliveryOption: boolean;
  showSmsOptInMessage: boolean;
  twilioPhoneNumber: string | null;
  selectedScheduleWindow?: ScheduleWindow;
  justAddedVariantId?: number;
  continueShopping?: ContinueShopping;
  showContinueShopping?: boolean;
};

export const initialState: CartState = {
  changes: [],
  data: null,
  isCheckingPhone: false,
  phoneError: null,
  selectedForDeliveryOption: false,
  sessionId: "",
  showSmsOptInMessage: false,
  twilioPhoneNumber: null,
};

export const CREATE_CART_SESSION_ID = "CREATE_CART_SESSION_ID";

export const ADD_TO_CART_SUCCESS = "ADD_TO_CART_SUCCESS";
export const ADD_TO_CART_DISMISS = "ADD_TO_CART_DISMISS";

export const GET_CART_SUCCESS = "GET_CART_SUCCESS";

export const REFRESH_CART_SUCCESS = "REFRESH_CART_SUCCESS";

export const UPDATE_CART_SUCCESS = "UPDATE_CART_SUCCESS";

export const SAVE_ORDER_REQUEST = "SAVE_ORDER_REQUEST";
export const SAVE_ORDER_SUCCESS = "SAVE_ORDER_SUCCESS";
export const SAVE_ORDER_FAILURE = "SAVE_ORDER_FAILURE";

export const SET_FOR_DELIVERY_OPTION = "SET_FOR_DELIVERY_OPTION";
export const SELECTED_SCHEDULE_WINDOW = "SELECTED_SCHEDULE_WINDOW";

export type SetDeliveryWindow = {
  type: typeof SELECTED_SCHEDULE_WINDOW;
  selectedScheduleWindow?: ScheduleWindow;
};

export type CreateCartSessionIdAction = {
  type: typeof CREATE_CART_SESSION_ID;
  countryCode: string;
};

export type AddToCartSuccessAction = {
  type: typeof ADD_TO_CART_SUCCESS;
  variantId: number;
};

export type AddToCartDismissAction = {
  type: typeof ADD_TO_CART_DISMISS;
};

export type GetCartSuccessAction = {
  type: typeof GET_CART_SUCCESS;
  cart: Cart;
  changes: CartChange[] | null;
  continueShopping?: ContinueShopping;
  showContinueShopping?: boolean;
};

export type RefreshCartSuccessAction = {
  type: typeof REFRESH_CART_SUCCESS;
  cart: Cart | null;
  changes: CartChange[] | null;
};

export type UpdateCartSuccessAction = {
  type: typeof UPDATE_CART_SUCCESS;
  cart: Cart;
  changes: CartChange[];
};

export type SaveReservationSuccessAction = {
  type: typeof SAVE_ORDER_SUCCESS;
};

export type SaveReservationFailureAction = {
  type: typeof SAVE_ORDER_FAILURE;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  payload: any;
};

export type SetForDeliveryOptionAction = {
  type: typeof SET_FOR_DELIVERY_OPTION;
  forDelivery: boolean;
};

export type CartActions =
  | CreateCartSessionIdAction
  | AddToCartSuccessAction
  | AddToCartDismissAction
  | GetCartSuccessAction
  | RefreshCartSuccessAction
  | UpdateCartSuccessAction
  | SaveReservationSuccessAction
  | SaveReservationFailureAction
  | SetDeliveryWindow
  | SetForDeliveryOptionAction
  | HydrateAction;

const createCartId = (countryCode: string) => {
  const sessionId = crypto.randomUUID();

  setCookie(X_LEAFLY_COOKIE_NAME, sessionId, {
    domain: countryCode === "CA" ? cookieDomainCa : cookieDomain,
    expires: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
    path: "/",
  });

  return sessionId;
};

export const cartReducer = (
  state = initialState,
  action: LoadCookiesAction | CartActions,
): CartState => {
  switch (action.type) {
    case HYDRATE:
      /**
       * The `cart` state is modified in `getServerSideProps` of checkout pages,
       * and needs to be loaded here to ensure that data is added to the redux state.
       */
      return {
        /**
         * Retain any state that was added client-side that isn't in the initial state.
         */
        ...state,
        ...action.payload.cart,
        /**
         * Avoid overriding the sessionId state, which is added to state on the client
         * with the CREATE_CART_SESSION_ID action.
         */
        sessionId: state.sessionId,
      };
    case LOAD_COOKIES:
      return {
        ...state,
        // * Don't override a session id that may be set via GET_CART_SUCCESS server side.
        sessionId: state.sessionId || action.cookies[X_LEAFLY_COOKIE_NAME],
      };
    case CREATE_CART_SESSION_ID:
      return {
        ...state,
        sessionId:
          state.sessionId ||
          state.data?.sessionId ||
          createCartId(action.countryCode),
      };
    case ADD_TO_CART_SUCCESS:
      return {
        ...state,
        justAddedVariantId: action.variantId,
      };
    case ADD_TO_CART_DISMISS:
      return {
        ...state,
        justAddedVariantId: undefined,
      };
    case GET_CART_SUCCESS:
      return {
        ...state,
        changes: action.changes,
        continueShopping: action.continueShopping,
        data: action.cart,
        sessionId: action.cart?.sessionId || state.sessionId,
        showContinueShopping: action.showContinueShopping,
      };
    case REFRESH_CART_SUCCESS:
      return {
        ...state,
        changes: action.changes,
        data: action.cart,
      };
    case UPDATE_CART_SUCCESS:
      return {
        ...state,
        data: action.cart,
      };
    case SAVE_ORDER_SUCCESS:
      // * When an order is created, clear the frontend bag state.
      return {
        ...initialState,
      };
    case SET_FOR_DELIVERY_OPTION:
      return {
        ...state,
        selectedForDeliveryOption: action.forDelivery,
      };
    case SELECTED_SCHEDULE_WINDOW:
      return {
        ...state,
        selectedScheduleWindow: action.selectedScheduleWindow,
      };

    default:
      return state;
  }
};

export default cartReducer;
